import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useLocale } from '../contexts/LocaleContext'
import {
  FileText,
  Library,
  Settings,
  Plus,
  Search,
  Eye,
  MoreVertical,
  Trash2,
  Copy,
  Download,
  Menu,
  X,
  Bell,
  User,
  Sparkles,
  Pencil,
  Link2,
  CheckCircle2,
  Briefcase,
  Shield,
  Palette,
  Zap,
  LayoutGrid,
  Send,
  Clock,
  Loader2,
  RefreshCw,
  AlertCircle,
  Flame,
  ArrowRight,
  FileClock,
  FileX,
  List,
  Grid3X3,
  Calendar,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  CheckSquare,
  Square,
  Activity,
  Keyboard,
  Check,
  Monitor,
  Smartphone,
  Tablet,
  BarChart3,
  Timer,
  ShieldCheck,
  MessageSquare
} from 'lucide-react'
import { Block } from '../components/BlockEditor'
import { proposalTemplates } from '../components/BlockEditor/templates'
import { toast } from 'sonner'
import api from '../lib/api'
import type { ProposalAnalytics, ProposalComment, UserNotification } from '../lib/api'
import AuditTrailModal from '../components/AuditTrailModal'
import DashboardAnalytics from '../components/DashboardAnalytics'
import ProposalSidePanel from '../components/dashboard/ProposalSidePanel'
import {
  SentIcon,
  ViewedIcon,
  SignedIcon,
  NotificationsIcon,
} from '../components/icons/CoreIcons'

// Proposal type from API
interface Proposal {
  id: string
  proposal_number: string
  proposal_title: string
  client_name: string
  client_company?: string
  client_email?: string
  total_price: number
  currency: string
  status: string
  view_count: number
  created_at: string
  updated_at: string
  share_token?: string
  signed_at?: string
  signer_name?: string
  viewed_at?: string
  sent_at?: string
  valid_until?: string
}

// High-tech color scheme status config
const statusConfig: Record<string, { label: string; bg: string; text: string; dot: string; icon: React.ReactNode }> = {
  draft: { label: 'Draft', bg: 'bg-slate-100', text: 'text-slate-600', dot: 'bg-slate-400', icon: <FileClock className="w-4 h-4" /> },
  sent: { label: 'Sent', bg: 'bg-slate-50', text: 'text-slate-800', dot: 'bg-slate-500', icon: <SentIcon className="w-4 h-4" /> },
  viewed: { label: 'Viewed', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', icon: <ViewedIcon className="w-4 h-4" /> },
  signed: { label: 'Signed', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: <SignedIcon className="w-4 h-4" /> },
  accepted: { label: 'Accepted', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', icon: <CheckCircle2 className="w-4 h-4" /> },
  declined: { label: 'Declined', bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500', icon: <FileX className="w-4 h-4" /> },
  expired: { label: 'Expired', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', icon: <AlertCircle className="w-4 h-4" /> },
  rejected: { label: 'Rejected', bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500', icon: <FileX className="w-4 h-4" /> },
}

// Filter tabs configuration
type FilterTab = 'all' | 'attention' | 'signed' | 'drafts'

const filterTabs: { id: FilterTab; label: string; icon: React.ReactNode }[] = [
  { id: 'all', label: 'All', icon: <LayoutGrid className="w-4 h-4" /> },
  { id: 'attention', label: 'Needs Attention', icon: <Flame className="w-4 h-4" /> },
  { id: 'signed', label: 'Signed', icon: <SignedIcon className="w-4 h-4" /> },
  { id: 'drafts', label: 'Drafts', icon: <FileClock className="w-4 h-4" /> },
]

// Sorting options
type SortField = 'date' | 'amount' | 'status' | 'client' | 'views'
type SortDirection = 'asc' | 'desc'

const sortOptions: { id: SortField; label: string }[] = [
  { id: 'date', label: 'Date' },
  { id: 'amount', label: 'Amount' },
  { id: 'status', label: 'Status' },
  { id: 'client', label: 'Client' },
  { id: 'views', label: 'Views' },
]

// Tags/Labels configuration
const tagColors = {
  vip: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'VIP' },
  urgent: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Urgent' },
  followup: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Follow-up' },
  important: { bg: 'bg-rose-100', text: 'text-rose-700', label: 'Important' },
  new: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'New' },
}

interface DashboardProps {
  onNewProposal?: (blocks?: Block[]) => void
  onEditProposal?: (proposalId: string) => void
  onOpenTemplates?: () => void
  hideSidebar?: boolean
}

const templateIcons: Record<string, React.ReactNode> = {
  FileText: <FileText className="w-5 h-5" />,
  Briefcase: <Briefcase className="w-5 h-5" />,
  Shield: <Shield className="w-5 h-5" />,
  Palette: <Palette className="w-5 h-5" />,
  Plus: <Plus className="w-5 h-5" />
}

const templateColors: Record<string, string> = {
  blue: 'bg-slate-500',
  purple: 'bg-slate-500',
  emerald: 'bg-cyan-500',
  pink: 'bg-fuchsia-500',
  orange: 'bg-orange-500',
  slate: 'bg-slate-600'
}

export default function Dashboard({ onNewProposal, onEditProposal, onOpenTemplates, hideSidebar = false }: DashboardProps) {
  const { tx } = useLocale()
  const [activeMenu, setActiveMenu] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [showTemplates, setShowTemplates] = useState(false)
  const [activeFilter, setActiveFilter] = useState<FilterTab>('all')
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards')

  // Sorting state
  const [sortField, setSortField] = useState<SortField>('date')
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc')
  const [showSortMenu, setShowSortMenu] = useState(false)
  const sortMenuRef = useRef<HTMLDivElement>(null)

  // Bulk selection state
  const [selectedProposals, setSelectedProposals] = useState<Set<string>>(new Set())

  // Command Center: selected proposal for side panel
  const [selectedProposalId, setSelectedProposalId] = useState<string | null>(null)

  // Activity feed state
  const [showActivityFeed, setShowActivityFeed] = useState(false)

  // Keyboard shortcuts modal
  const [showShortcuts, setShowShortcuts] = useState(false)

  // Proposal tags (stored locally for now)
  const [proposalTags, setProposalTags] = useState<Record<string, string[]>>({})

  // Tag menu state - which proposal's tag menu is open
  const [openTagMenu, setOpenTagMenu] = useState<string | null>(null)

  // Notifications (bell dropdown)
  const [showNotificationsDropdown, setShowNotificationsDropdown] = useState(false)
  const [notifications, setNotifications] = useState<UserNotification[]>([])
  const [notificationsLoading, setNotificationsLoading] = useState(false)
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0)
  const notificationsRef = useRef<HTMLDivElement>(null)

  // API state
  const [proposals, setProposals] = useState<Proposal[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [operatingId, setOperatingId] = useState<string | null>(null)

  // Comments state
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({})
  const [showCommentsPanel, setShowCommentsPanel] = useState<string | null>(null)
  const [panelComments, setPanelComments] = useState<ProposalComment[]>([])
  const [panelCommentsLoading, setPanelCommentsLoading] = useState(false)
  const [ownerReply, setOwnerReply] = useState('')
  const [sendingReply, setSendingReply] = useState(false)

  // Fetch proposals from API
  const fetchProposals = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await api.getProposals()
      const loaded = response.proposals as Proposal[]

      // Auto-expire: if valid_until has passed and proposal isn't in a terminal state
      const terminalStatuses = new Set(['signed', 'accepted', 'declined', 'expired', 'rejected'])
      const nowISO = new Date().toISOString()
      const toExpire = loaded.filter(p =>
        p.valid_until &&
        p.valid_until < nowISO &&
        !terminalStatuses.has(p.status)
      )

      if (toExpire.length > 0) {
        const results = await Promise.allSettled(toExpire.map(p => api.updateProposalStatus(p.id, 'expired')))
        // Only update UI for proposals that were successfully expired
        const succeededIds = new Set(
          toExpire.filter((_, i) => results[i].status === 'fulfilled').map(p => p.id)
        )
        setProposals(loaded.map(p => succeededIds.has(p.id) ? { ...p, status: 'expired' } : p))
      } else {
        setProposals(loaded)
      }
    } catch (err) {
      console.error('Failed to fetch proposals:', err)
      setError(err instanceof Error ? err.message : 'Failed to load proposals')
    } finally {
      setLoading(false)
    }
  }, [])

  // Fetch unread comment counts
  const fetchUnreadCounts = useCallback(async () => {
    try {
      const response = await api.getUnreadCommentCounts()
      setUnreadCounts(response.unread_counts || {})
    } catch {
      // Silently fail - comments badge is non-critical
    }
  }, [])

  // Fetch notifications and unread count
  const fetchNotifications = useCallback(async () => {
    setNotificationsLoading(true)
    try {
      const [listRes, countRes] = await Promise.all([
        api.getNotifications(20, false),
        api.getUnreadNotificationCount(),
      ])
      setNotifications(listRes.notifications || [])
      setUnreadNotificationCount(countRes.count ?? 0)
    } catch {
      // Silently fail
    } finally {
      setNotificationsLoading(false)
    }
  }, [])

  const fetchUnreadNotificationCount = useCallback(async () => {
    try {
      const res = await api.getUnreadNotificationCount()
      setUnreadNotificationCount(res.count ?? 0)
    } catch {
      // Silently fail
    }
  }, [])

  // Load proposals and comments on mount
  useEffect(() => {
    fetchProposals()
    fetchUnreadCounts()
    fetchUnreadNotificationCount()
  }, [fetchProposals, fetchUnreadCounts, fetchUnreadNotificationCount])

  // Load notifications when dropdown opens
  useEffect(() => {
    if (showNotificationsDropdown) {
      fetchNotifications()
    }
  }, [showNotificationsDropdown, fetchNotifications])

  // Load comments for a specific proposal in the panel
  const openCommentsForProposal = useCallback(async (proposalId: string) => {
    setShowCommentsPanel(proposalId)
    setPanelCommentsLoading(true)
    try {
      const response = await api.getProposalComments(proposalId)
      setPanelComments(response.comments || [])
      // Mark all as read
      const unreadComments = (response.comments || []).filter((c: ProposalComment) => !c.is_read && !c.is_owner)
      for (const comment of unreadComments) {
        try {
          await api.markCommentRead(proposalId, comment.id)
        } catch {
          // Silently fail
        }
      }
      // Update unread counts
      setUnreadCounts(prev => {
        const updated = { ...prev }
        delete updated[proposalId]
        return updated
      })
    } catch {
      toast.error(tx('Failed to load comments', 'נכשל בטעינת התגובות'))
    } finally {
      setPanelCommentsLoading(false)
    }
  }, [])

  const handleSendOwnerReply = async () => {
    if (!ownerReply.trim() || !showCommentsPanel) return
    setSendingReply(true)
    try {
      await api.addProposalComment(showCommentsPanel, {
        author_name: 'Proposal Owner',
        content: ownerReply.trim(),
        is_owner: true,
      })
      setOwnerReply('')
      toast.success(tx('Reply sent', 'התשובה נשלחה'))
      // Reload comments
      const response = await api.getProposalComments(showCommentsPanel)
      setPanelComments(response.comments || [])
    } catch {
      toast.error(tx('Failed to send reply', 'נכשל בשליחת התשובה'))
    } finally {
      setSendingReply(false)
    }
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-menu-container]')) {
        setActiveMenu(null)
      }
      if (sortMenuRef.current && !sortMenuRef.current.contains(target)) {
        setShowSortMenu(false)
      }
      if (notificationsRef.current && !notificationsRef.current.contains(target)) {
        setShowNotificationsDropdown(false)
      }
      // Close tag menu if clicking outside
      if (!target.closest('[data-tag-menu]')) {
        setOpenTagMenu(null)
      }
    }

    if (activeMenu || showSortMenu || openTagMenu || showNotificationsDropdown) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [activeMenu, showSortMenu, openTagMenu, showNotificationsDropdown])


  // Filter proposals based on active tab and search
  const getFilteredProposals = () => {
    let filtered = proposals

    // Apply search filter
    if (searchQuery) {
      filtered = filtered.filter((p) =>
        p.proposal_title?.includes(searchQuery) ||
        p.client_name?.includes(searchQuery) ||
        p.client_company?.includes(searchQuery)
      )
    }

    // Apply tab filter
    switch (activeFilter) {
      case 'attention':
        // Hot leads: viewed but not signed, or sent but not viewed for a while
        return filtered.filter(p =>
          p.status === 'viewed' ||
          (p.status === 'sent' && p.view_count > 0)
        )
      case 'signed':
        return filtered.filter(p => p.status === 'signed' || p.status === 'accepted' || p.signed_at)
      case 'drafts':
        return filtered.filter(p => p.status === 'draft')
      default:
        return filtered
    }
  }

  const filteredProposals = getFilteredProposals()

  // Sort proposals
  const sortedProposals = useMemo(() => {
    const sorted = [...filteredProposals]
    sorted.sort((a, b) => {
      let comparison = 0
      switch (sortField) {
        case 'date':
          comparison = new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          break
        case 'amount':
          comparison = (b.total_price || 0) - (a.total_price || 0)
          break
        case 'status':
          const statusOrder: Record<string, number> = { draft: 0, sent: 1, viewed: 2, signed: 3, rejected: 4 }
          comparison = (statusOrder[a.status] || 0) - (statusOrder[b.status] || 0)
          break
        case 'client':
          comparison = a.client_name.localeCompare(b.client_name, 'en')
          break
        case 'views':
          comparison = (b.view_count || 0) - (a.view_count || 0)
          break
      }
      return sortDirection === 'asc' ? -comparison : comparison
    })
    return sorted
  }, [filteredProposals, sortField, sortDirection])

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      if (e.key === 'n' && !e.ctrlKey && !e.metaKey) {
        e.preventDefault()
        onNewProposal?.()
      }
      if (e.key === '/') {
        e.preventDefault()
        document.querySelector<HTMLInputElement>('[data-search-input]')?.focus()
      }
      if (e.key === 'Escape') {
        setSelectedProposals(new Set())
        setShowShortcuts(false)
        setShowActivityFeed(false)
        setShowSortMenu(false)
      }
      if (e.key === '?' && e.shiftKey) {
        e.preventDefault()
        setShowShortcuts(true)
      }
      if ((e.ctrlKey || e.metaKey) && e.key === 'a' && sortedProposals.length > 0) {
        e.preventDefault()
        if (selectedProposals.size === sortedProposals.length) {
          setSelectedProposals(new Set())
        } else {
          setSelectedProposals(new Set(sortedProposals.map(p => p.id)))
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onNewProposal, sortedProposals, selectedProposals])

  // Calculate stats
  const hotLeads = proposals.filter(p => p.status === 'viewed' || (p.status === 'sent' && p.view_count > 0))
  const waitingForView = proposals.filter(p => p.status === 'sent' && p.view_count === 0)
  const signedProposals = proposals.filter(p => p.status === 'signed' || p.signed_at)
  const draftProposals = proposals.filter(p => p.status === 'draft')

  // Group proposals by month
  const groupedProposals = useMemo(() => {
    const groups: Record<string, Proposal[]> = {}
    sortedProposals.forEach(proposal => {
      const date = new Date(proposal.created_at)
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      if (!groups[monthKey]) {
        groups[monthKey] = []
      }
      groups[monthKey].push(proposal)
    })
    return Object.entries(groups)
      .sort(([a], [b]) => b.localeCompare(a))
      .map(([key, items]) => ({
        key,
        label: new Date(key + '-01').toLocaleDateString('en-US', { year: 'numeric', month: 'long' }),
        proposals: items
      }))
  }, [sortedProposals])

  // Generate activity feed from proposals
  const activityFeed = useMemo(() => {
    const activities: { id: string; type: string; proposal: Proposal; date: Date }[] = []
    proposals.forEach(p => {
      if (p.signed_at) {
        activities.push({ id: `${p.id}-signed`, type: 'signed', proposal: p, date: new Date(p.signed_at) })
      }
      if (p.viewed_at) {
        activities.push({ id: `${p.id}-viewed`, type: 'viewed', proposal: p, date: new Date(p.viewed_at) })
      }
      if (p.sent_at) {
        activities.push({ id: `${p.id}-sent`, type: 'sent', proposal: p, date: new Date(p.sent_at) })
      }
    })
    return activities.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 10)
  }, [proposals])

  // Bulk selection handlers
  const toggleSelectProposal = (id: string) => {
    setSelectedProposals(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const selectAllProposals = () => {
    if (selectedProposals.size === sortedProposals.length) {
      setSelectedProposals(new Set())
    } else {
      setSelectedProposals(new Set(sortedProposals.map(p => p.id)))
    }
  }

  const handleBulkDelete = async () => {
    if (selectedProposals.size === 0) return
    if (!confirm(tx(`Are you sure you want to delete ${selectedProposals.size} proposals?`, `האם אתה בטוח שברצונך למחוק ${selectedProposals.size} הצעות?`))) return

    for (const id of selectedProposals) {
      try {
        await api.deleteProposal(id)
      } catch (err) {
        console.error(`Failed to delete proposal ${id}:`, err)
      }
    }
    setProposals(prev => prev.filter(p => !selectedProposals.has(p.id)))
    setSelectedProposals(new Set())
    toast.success(tx('Proposals deleted successfully', 'ההצעות נמחקו בהצלחה'))
  }

  // Tag management
  const toggleTag = (proposalId: string, tag: string) => {
    setProposalTags(prev => {
      const currentTags = prev[proposalId] || []
      const newTags = currentTags.includes(tag)
        ? currentTags.filter(t => t !== tag)
        : [...currentTags, tag]
      return { ...prev, [proposalId]: newTags }
    })
  }

  // Delete proposal handler
  const handleDelete = async (id: string) => {
    if (!confirm(tx('Are you sure you want to delete this proposal?', 'האם אתה בטוח שברצונך למחוק הצעה זו?'))) return
    try {
      await api.deleteProposal(id)
      setProposals(prev => prev.filter(p => p.id !== id))
      setActiveMenu(null)
      if (selectedProposalId === id) setSelectedProposalId(null)
      toast.success(tx('Proposal deleted successfully', 'ההצעה נמחקה בהצלחה'))
    } catch (err) {
      toast.error(tx('Failed to delete proposal', 'נכשל במחיקת ההצעה'))
    }
  }

  // Copy share link handler
  const handleCopyLink = (shareToken: string) => {
    const url = `${window.location.origin}/view/${shareToken}`
    navigator.clipboard.writeText(url)
    toast.success(tx('Link copied!', 'הקישור הועתק!'))
    setActiveMenu(null)
  }

  // Resolve selected proposal; clear selection if no longer in filtered list
  const selectedProposal = useMemo(() => {
    if (!selectedProposalId) return null
    const p = filteredProposals.find(x => x.id === selectedProposalId)
    return p || null
  }, [selectedProposalId, filteredProposals])

  useEffect(() => {
    if (selectedProposalId && !filteredProposals.some(p => p.id === selectedProposalId)) {
      setSelectedProposalId(null)
    }
  }, [selectedProposalId, filteredProposals])

  // Accept / Decline handlers (manual override)
  const handleAccept = async (id: string) => {
    if (operatingId) return
    setOperatingId(id)
    try {
      await api.updateProposalStatus(id, 'accepted')
      setProposals(prev => prev.map(p => p.id === id ? { ...p, status: 'accepted' } : p))
      toast.success(tx('Proposal marked as accepted', 'ההצעה סומנה כמאושרת'))
    } catch {
      toast.error(tx('Failed to update status', 'עדכון הסטטוס נכשל'))
    } finally {
      setOperatingId(null)
    }
  }

  const handleDecline = async (id: string) => {
    if (operatingId) return
    setOperatingId(id)
    try {
      await api.updateProposalStatus(id, 'declined')
      setProposals(prev => prev.map(p => p.id === id ? { ...p, status: 'declined' } : p))
      toast.success(tx('Proposal marked as declined', 'ההצעה סומנה כנדחתה'))
    } catch {
      toast.error(tx('Failed to update status', 'עדכון הסטטוס נכשל'))
    } finally {
      setOperatingId(null)
    }
  }

  // Send reminder handler
  const handleSendReminder = async (proposalId: string) => {
    try {
      setActiveMenu(null)
      const result = await api.sendReminder(proposalId)
      if (result.success) {
        toast.success(tx('Reminder sent successfully!', 'התזכורת נשלחה בהצלחה!'))
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : tx('Failed to send reminder', 'נכשל בשליחת התזכורת'))
    }
  }

  return (
    <div className={`flex ${hideSidebar ? 'min-h-full' : 'h-screen-safe'} bg-slate-50`} dir="ltr">
      {/* Mobile Sidebar Overlay - only when sidebar is shown */}
      {!hideSidebar && sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Dark theme - only when not hidden */}
      {!hideSidebar && (
        <aside className={`
          fixed lg:static inset-y-0 right-0 z-50
          w-64 lg:w-20 bg-slate-900 border-l border-slate-800
          transform transition-transform duration-300
          ${sidebarOpen ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}
          flex flex-col
        `}>
          {/* Logo */}
          <div className="p-4 lg:p-3 border-b border-slate-800">
            <div className="flex items-center gap-3 lg:justify-center">
              <div className="w-10 h-10 bg-gradient-to-br from-slate-500 to-slate-700 rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-white lg:hidden">Quor</span>
              <button
                onClick={() => setSidebarOpen(false)}
                className="mr-auto lg:hidden p-2 hover:bg-slate-800 rounded-lg text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-2 space-y-1">
            <SidebarItem icon={<LayoutGrid />} label="Proposals" active />
            <SidebarItem icon={<Library />} label="Templates" onClick={onOpenTemplates} />
            <SidebarItem icon={<Send />} label="Sent" badge={waitingForView.length || undefined} />
            <SidebarItem icon={<Sparkles />} label="AI" isNew />
          </nav>

          {/* Bottom */}
          <div className="p-2 border-t border-slate-800">
            <SidebarItem icon={<Settings />} label="Settings" />
          </div>
        </aside>
      )}

      {/* Main Content */}
      <main className={`flex-1 flex flex-col min-w-0 ${hideSidebar ? '' : 'overflow-y-auto'}`}>
        {/* Top Header */}
        <header className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3 sticky top-0 z-30">
          <div className="flex items-center justify-between gap-4">
            {/* Mobile Menu Button - only when sidebar is shown */}
            {!hideSidebar && (
              <button
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 hover:bg-slate-100 rounded-lg"
              >
                <Menu className="w-5 h-5 text-slate-600" />
              </button>
            )}

            {/* Title */}
            <div className="flex-1 min-w-0">
              <h1 className="text-xl lg:text-2xl font-bold text-slate-800 truncate">
                {tx('My Proposals', 'ההצעות שלי')}
              </h1>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 lg:gap-3">
              {/* New Proposal Button - Primary Action in Header */}
              <button
                onClick={() => onNewProposal?.()}
                className="hidden sm:flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-accent-500 text-accent-600 rounded-xl hover:bg-accent-50 font-semibold transition-all"
              >
                <Plus className="w-5 h-5" />
                <span>{tx('New Proposal', 'הצעה חדשה')}</span>
              </button>

              <div className="relative" ref={notificationsRef}>
                <button
                  onClick={() => setShowNotificationsDropdown(!showNotificationsDropdown)}
                  className="p-2 hover:bg-slate-100 rounded-lg relative"
                >
                  <NotificationsIcon className="w-5 h-5 text-slate-500" />
                  {unreadNotificationCount > 0 && (
                    <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold bg-slate-500 text-white rounded-full">
                      {unreadNotificationCount > 99 ? '99+' : unreadNotificationCount}
                    </span>
                  )}
                  {unreadNotificationCount === 0 && hotLeads.length > 0 && (
                    <span className="absolute top-1 right-1 w-2.5 h-2.5 bg-orange-500 rounded-full animate-pulse" />
                  )}
                </button>
                {showNotificationsDropdown && (
                  <div className="absolute right-0 top-full mt-2 w-80 max-w-[calc(100vw-2rem)] max-h-[400px] bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                      <h3 className="font-semibold text-slate-800">{tx('Notifications', 'התראות')}</h3>
                      {unreadNotificationCount > 0 && (
                        <button
                          onClick={async () => {
                            try {
                              await api.markAllNotificationsRead()
                              setUnreadNotificationCount(0)
                              setNotifications(prev => prev.map(n => ({ ...n, is_read: true })))
                              toast.success(tx('Marked as read', 'סומן כנקרא'))
                            } catch {
                              toast.error(tx('Error', 'שגיאה'))
                            }
                          }}
                          className="text-xs text-slate-700 hover:underline"
                        >
                          {tx('Mark all as read', 'סמן הכל כנקרא')}
                        </button>
                      )}
                    </div>
                    <div className="max-h-[320px] overflow-y-auto">
                      {notificationsLoading ? (
                        <div className="flex justify-center py-8">
                          <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
                        </div>
                      ) : notifications.length === 0 ? (
                        <div className="text-center py-8 text-slate-400 text-sm">{tx('No notifications', 'אין התראות')}</div>
                      ) : (
                        notifications.map(n => (
                          <div
                            key={n.id}
                            onClick={async () => {
                              if (!n.is_read) {
                                try {
                                  await api.markNotificationRead(n.id)
                                  setUnreadNotificationCount(prev => Math.max(0, prev - 1))
                                  setNotifications(prev => prev.map(x => x.id === n.id ? { ...x, is_read: true } : x))
                                } catch {
                                  /* ignore */
                                }
                              }
                            }}
                            className={`px-4 py-3 hover:bg-slate-50 cursor-pointer border-b border-slate-50 last:border-0 ${!n.is_read ? 'bg-slate-50/50' : ''}`}
                          >
                            <p className={`text-sm font-medium ${!n.is_read ? 'text-slate-800' : 'text-slate-600'}`}>{n.title}</p>
                            <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{n.content}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString('en-US')}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <button className="p-2 hover:bg-slate-100 rounded-lg hidden sm:block">
                <User className="w-5 h-5 text-slate-500" />
              </button>
            </div>
          </div>
        </header>

        {/* Hot Leads Alert - Only show when there are hot leads */}
        {hotLeads.length > 0 && (
          <div className="px-4 lg:px-6 pt-4">
            <div className="bg-gradient-to-l from-orange-500/10 via-amber-500/10 to-yellow-500/10 border border-orange-200 rounded-2xl p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center shrink-0">
                  <Flame className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-800 flex items-center gap-2">
                    {hotLeads.length} {tx('Hot Leads!', 'לידים חמים!')}
                    <span className="text-xs font-normal text-orange-600 bg-orange-100 px-2 py-0.5 rounded-full">
                      {tx('Awaiting signature', 'ממתין לחתימה')}
                    </span>
                  </h3>
                  <p className="text-sm text-slate-600 mt-0.5">
                    {tx('Proposals viewed by clients - now is the time to act!', 'הצעות שנצפו על ידי לקוחות - זה הזמן לפעול!')}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setActiveFilter('attention')
                    setTimeout(() => {
                      document.getElementById('proposals-filter')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                    }, 100)
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-orange-200 text-orange-700 rounded-lg hover:bg-orange-50 transition-colors font-medium text-sm shrink-0"
                >
                  {tx('View All', 'צפה בכולם')}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Enhanced Dashboard Analytics */}
        <DashboardAnalytics proposals={proposals} simpleMode={false} />

        {/* Filter Tabs & Search */}
        <div id="proposals-filter" className="px-4 lg:px-6 pb-4">
          {/* Tabs */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex items-center gap-1 p-1 bg-slate-100 rounded-xl overflow-x-auto flex-1">
            {filterTabs.map((tab) => {
              const count = tab.id === 'attention' ? hotLeads.length :
                           tab.id === 'signed' ? signedProposals.length :
                           tab.id === 'drafts' ? draftProposals.length : proposals.length
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveFilter(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap transition-all ${
                    activeFilter === tab.id
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  {count > 0 && (
                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                      activeFilter === tab.id
                        ? tab.id === 'attention' ? 'bg-orange-100 text-orange-700' : 'bg-slate-100 text-slate-800'
                        : 'bg-slate-200 text-slate-600'
                    }`}>
                      {count}
                    </span>
                  )}
                </button>
              )
            })}
            </div>
          </div>

          {/* Search & Controls Row */}
          <div className="flex items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder={tx('Search proposals...', 'חיפוש הצעות...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                data-search-input
                className="w-full pr-10 pl-4 py-2.5 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400 transition-all"
              />
            </div>

            {/* Sort Dropdown */}
            <div className="relative" ref={sortMenuRef}>
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-2 px-3 py-2.5 bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-all text-sm"
              >
                <ArrowUpDown className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600 hidden sm:inline">{sortOptions.find(s => s.id === sortField)?.label}</span>
                {sortDirection === 'asc' ? <ArrowUp className="w-3 h-3 text-slate-400" /> : <ArrowDown className="w-3 h-3 text-slate-400" />}
              </button>
              {showSortMenu && (
                <div className="absolute left-0 top-full mt-2 w-40 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50">
                  {sortOptions.map((option) => (
                    <button
                      key={option.id}
                      onClick={() => {
                        if (sortField === option.id) {
                          setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')
                        } else {
                          setSortField(option.id)
                          setSortDirection('desc')
                        }
                        setShowSortMenu(false)
                      }}
                      className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${
                        sortField === option.id ? 'bg-slate-50 text-slate-800' : 'text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      <span>{option.label}</span>
                      {sortField === option.id && (
                        sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Activity Feed Toggle */}
            <button
              onClick={() => setShowActivityFeed(!showActivityFeed)}
              className={`p-2.5 rounded-xl border transition-all ${
                showActivityFeed
                  ? 'bg-slate-100 border-slate-200 text-slate-700'
                  : 'bg-white border-slate-200 text-slate-400 hover:text-slate-600'
              }`}
              title={tx('Recent Activity', 'פעילות אחרונה')}
            >
              <Activity className="w-4 h-4" />
            </button>

            {/* Keyboard Shortcuts */}
            <button
              onClick={() => setShowShortcuts(true)}
              className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-slate-600 transition-all hidden sm:block"
              title={tx('Keyboard shortcuts (?)', 'קיצורי מקלדת (?)')}
            >
              <Keyboard className="w-4 h-4" />
            </button>

            {/* View Toggle */}
            <div className="flex items-center bg-white border border-slate-200 rounded-xl p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'cards'
                    ? 'bg-slate-100 text-slate-700'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
                title={tx('Card view', 'תצוגת כרטיסים')}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded-lg transition-all ${
                  viewMode === 'table'
                    ? 'bg-slate-100 text-slate-700'
                    : 'text-slate-400 hover:text-slate-600'
                }`}
                title={tx('Table view', 'תצוגת טבלה')}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Bulk Actions Bar */}
          {selectedProposals.size > 0 && (
            <div className="mt-3 flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl">
              <button
                onClick={selectAllProposals}
                className="flex items-center gap-2 text-sm text-slate-800"
              >
                {selectedProposals.size === sortedProposals.length ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                <span>{selectedProposals.size} {tx('selected', 'נבחרו')}</span>
              </button>
              <div className="h-4 w-px bg-slate-200" />
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-100 rounded-lg transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" />
                {tx('Delete', 'מחק')}
              </button>
              <button
                onClick={() => setSelectedProposals(new Set())}
                className="mr-auto text-sm text-slate-500 hover:text-slate-700"
              >
                {tx('Clear selection', 'נקה בחירה')}
              </button>
            </div>
          )}
        </div>

        {/* Proposals List + Command Center Panel */}
        <div className="px-4 lg:px-6 pb-6 grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-6 items-start">
          <div className="min-w-0">
          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
              <Loader2 className="w-10 h-10 text-accent-500 animate-spin mx-auto mb-4" />
              <p className="text-slate-500">{tx('Loading proposals...', 'טוען הצעות...')}</p>
            </div>
          )}

          {/* Error State */}
          {error && !loading && (
            <div className="bg-white rounded-2xl p-12 text-center border border-rose-200">
              <div className="w-16 h-16 bg-rose-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-rose-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-800 mb-2">{tx('Loading Error', 'שגיאת טעינה')}</h3>
              <p className="text-slate-500 mb-4">{error}</p>
              <button
                onClick={fetchProposals}
                className="px-6 py-2.5 bg-gradient-to-r from-slate-700 to-slate-700 text-white rounded-xl hover:from-slate-800 hover:to-slate-800 font-medium inline-flex items-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                {tx('Try Again', 'נסה שוב')}
              </button>
            </div>
          )}

          {/* Empty State - No proposals at all */}
          {!loading && !error && proposals.length === 0 && (
            <div className="bg-white rounded-2xl p-12 text-center border border-slate-200">
              <div className="w-20 h-20 bg-gradient-to-br from-slate-100 to-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                <FileText className="w-10 h-10 text-slate-500" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">{tx('No proposals yet', 'אין הצעות עדיין')}</h3>
              <p className="text-slate-500 mb-6 max-w-sm mx-auto">
                {tx('Create your first proposal and start closing deals', 'צור את ההצעה הראשונה ותתחיל לסגור עסקאות')}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={() => onNewProposal?.()}
                  className="flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-white text-sm font-medium rounded-lg hover:bg-accent-600 transition-colors"
                >
                  <Sparkles className="w-4 h-4" />
                  {tx('Create with AI', 'צור עם AI')}
                </button>
                <button
                  onClick={() => onNewProposal?.()}
                  className="flex items-center gap-2 px-5 py-2.5 border border-slate-200 text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  {tx('Start from blank', 'התחל מדף ריק')}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-4">
                {tx('AI builds a complete proposal in seconds', 'AI בונה הצעה מלאה תוך שניות')}
              </p>
            </div>
          )}

          {/* Empty Filter State - Has proposals but filter is empty */}
          {!loading && !error && proposals.length > 0 && filteredProposals.length === 0 && (
            <div className="bg-white rounded-2xl p-10 text-center border border-slate-200">
              <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                {activeFilter === 'attention' && <Flame className="w-8 h-8 text-slate-400" />}
                {activeFilter === 'signed' && <CheckCircle2 className="w-8 h-8 text-slate-400" />}
                {activeFilter === 'drafts' && <FileClock className="w-8 h-8 text-slate-400" />}
              </div>
              <h3 className="text-lg font-semibold text-slate-700 mb-1">
                {activeFilter === 'attention' && tx('No hot leads right now', 'אין לידים חמים כרגע')}
                {activeFilter === 'signed' && tx('No signed proposals', 'אין הצעות חתומות')}
                {activeFilter === 'drafts' && tx('No drafts', 'אין טיוטות')}
              </h3>
              <p className="text-sm text-slate-400 mb-4">
                {activeFilter === 'attention' && tx('When clients view your proposals, they will appear here', 'כאשר לקוחות יצפו בהצעות שלך, הן יופיעו כאן')}
                {activeFilter === 'signed' && tx('Signed proposals will appear here', 'הצעות חתומות יופיעו כאן')}
                {activeFilter === 'drafts' && tx('Saved drafts will appear here', 'טיוטות שמורות יופיעו כאן')}
              </p>
              <button
                onClick={() => setActiveFilter('all')}
                className="text-slate-700 hover:text-slate-800 font-medium text-sm"
              >
                {tx('Back to all proposals', 'חזרה לכל ההצעות')}
              </button>
            </div>
          )}

          {/* Table View */}
          {!loading && !error && sortedProposals.length > 0 && viewMode === 'table' && (
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 w-10">
                      <button
                        onClick={selectAllProposals}
                        className="p-1 hover:bg-slate-200 rounded transition-colors"
                      >
                        {selectedProposals.size === sortedProposals.length && sortedProposals.length > 0 ? (
                          <CheckSquare className="w-4 h-4 text-slate-700" />
                        ) : (
                          <Square className="w-4 h-4 text-slate-400" />
                        )}
                      </button>
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{tx('Proposal', 'הצעה')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{tx('Client', 'לקוח')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{tx('Tags', 'תגיות')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{tx('Status', 'סטטוס')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{tx('Views', 'צפיות')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{tx('Amount', 'סכום')}</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase">{tx('Date', 'תאריך')}</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sortedProposals.map((proposal) => {
                    const status = statusConfig[proposal.status] || statusConfig.draft
                    const tags = proposalTags[proposal.id] || []
                    const isSelected = selectedProposals.has(proposal.id)
                    return (
                      <tr key={proposal.id} className={`transition-colors ${isSelected ? 'bg-slate-50' : 'hover:bg-slate-50'}`}>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleSelectProposal(proposal.id)}
                            className="p-1 hover:bg-slate-200 rounded transition-colors"
                          >
                            {isSelected ? (
                              <CheckSquare className="w-4 h-4 text-slate-700" />
                            ) : (
                              <Square className="w-4 h-4 text-slate-400" />
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800">{proposal.proposal_title}</div>
                          <div className="text-xs text-slate-400 font-mono">{proposal.proposal_number}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-slate-700">{proposal.client_name}</div>
                          {proposal.client_company && (
                            <div className="text-xs text-slate-400">{proposal.client_company}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 flex-wrap relative">
                            {tags.map(tag => {
                              const tagConfig = tagColors[tag as keyof typeof tagColors]
                              return tagConfig ? (
                                <button
                                  key={tag}
                                  onClick={() => toggleTag(proposal.id, tag)}
                                  className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${tagConfig.bg} ${tagConfig.text} hover:opacity-70 transition-opacity group flex items-center gap-0.5`}
                                  title={tx('Click to remove', 'לחץ להסרה')}
                                >
                                  {tagConfig.label}
                                  <X className="w-2.5 h-2.5 opacity-0 group-hover:opacity-100" />
                                </button>
                              ) : null
                            })}
                            <div className="relative" data-tag-menu>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  setOpenTagMenu(openTagMenu === proposal.id ? null : proposal.id)
                                }}
                                className="p-0.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
                                title={tx('Manage tags', 'ניהול תגיות')}
                              >
                                <Tag className="w-3 h-3" />
                              </button>
                              {openTagMenu === proposal.id && (
                                <div className="absolute top-full right-0 mt-1 w-32 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50" data-tag-menu>
                                  {Object.entries(tagColors).map(([key, config]) => {
                                    const isActive = tags.includes(key)
                                    return (
                                      <button
                                        key={key}
                                        onClick={() => {
                                          toggleTag(proposal.id, key)
                                        }}
                                        className={`w-full flex items-center gap-2 px-2 py-1.5 text-xs transition-colors ${
                                          isActive ? 'bg-slate-50' : 'hover:bg-slate-50'
                                        }`}
                                      >
                                        <span className={`w-3 h-3 rounded ${config.bg}`} />
                                        <span className="text-slate-700">{config.label}</span>
                                        {isActive && <Check className="w-3 h-3 text-emerald-500 mr-auto" />}
                                      </button>
                                    )
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium ${status.bg} ${status.text}`}>
                            {status.icon}
                            {status.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div
                            className="flex items-center gap-1.5"
                            title={proposal.viewed_at ? `Last viewed: ${formatRelativeTime(proposal.viewed_at)}` : ''}
                          >
                            <Eye className={`w-3.5 h-3.5 ${proposal.view_count > 0 ? 'text-cyan-500' : 'text-slate-300'}`} />
                            <span className={`text-sm ${proposal.view_count > 0 ? 'text-cyan-600 font-medium' : 'text-slate-400'}`}>
                              {proposal.view_count || 0}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 font-bold text-slate-800">
                          {proposal.total_price ? `${proposal.currency}${proposal.total_price.toLocaleString()}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">
                          {new Date(proposal.created_at).toLocaleDateString('en-US')}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            {unreadCounts[proposal.id] > 0 && (
                              <button
                                onClick={() => openCommentsForProposal(proposal.id)}
                                className="p-2 hover:bg-slate-50 rounded-lg transition-colors text-slate-500 hover:text-slate-800 relative"
                                title={tx('New comments', 'תגובות חדשות')}
                              >
                                <MessageSquare className="w-4 h-4" />
                                <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-slate-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                  {unreadCounts[proposal.id]}
                                </span>
                              </button>
                            )}
                            {(proposal.status === 'sent' || proposal.status === 'viewed') && (
                              <button
                                onClick={() => handleSendReminder(proposal.id)}
                                className="p-2 hover:bg-amber-50 rounded-lg transition-colors text-slate-400 hover:text-amber-600"
                                title={tx('Send reminder', 'שלח תזכורת')}
                              >
                                <Bell className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => onEditProposal?.(proposal.id)}
                              className="p-2 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Card View with Month Grouping */}
          {!loading && !error && sortedProposals.length > 0 && viewMode === 'cards' && (
            <div className="space-y-6">
              {groupedProposals.map((group) => (
                <div key={group.key}>
                  {/* Month Header */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-lg">
                      <Calendar className="w-4 h-4 text-slate-500" />
                      <span className="text-sm font-medium text-slate-700">{group.label}</span>
                    </div>
                    <div className="h-px flex-1 bg-slate-200" />
                    <span className="text-xs text-slate-400">{group.proposals.length} {tx('proposals', 'הצעות')}</span>
                  </div>
                  {/* Proposals */}
                  <div className="space-y-3">
                    {group.proposals.map((proposal) => (
                      <ProposalCard
                        key={proposal.id}
                        proposal={proposal}
                        isSelected={selectedProposalId === proposal.id}
                        onSelect={() => setSelectedProposalId(id => id === proposal.id ? null : proposal.id)}
                        isMenuOpen={activeMenu === proposal.id}
                        onMenuToggle={() => setActiveMenu(activeMenu === proposal.id ? null : proposal.id)}
                        onEdit={() => onEditProposal?.(proposal.id)}
                        onDelete={() => handleDelete(proposal.id)}
                        onCopyLink={() => proposal.share_token && handleCopyLink(proposal.share_token)}
                        onSendReminder={() => handleSendReminder(proposal.id)}
                        unreadComments={unreadCounts[proposal.id] || 0}
                        onCommentsClick={() => openCommentsForProposal(proposal.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>

          {/* Command Center Side Panel */}
          <ProposalSidePanel
            proposal={selectedProposal}
            onClose={() => setSelectedProposalId(null)}
            onEdit={(id) => onEditProposal?.(id)}
            onSendReminder={handleSendReminder}
            onCopyLink={handleCopyLink}
            onDelete={handleDelete}
            onAccept={handleAccept}
            onDecline={handleDecline}
            operatingId={operatingId}
          />
        </div>

        {/* Floating Action Button - Mobile only */}
        <button
          onClick={() => onNewProposal?.()}
          className="fixed bottom-6 left-6 w-14 h-14 bg-rose-500 border-2 border-rose-400 text-white rounded-2xl shadow-xl shadow-rose-500/30 hover:bg-rose-600 hover:scale-105 transition-all flex items-center justify-center z-40 lg:hidden"
        >
          <Plus className="w-7 h-7" />
        </button>
      </main>

      {/* Activity Feed Sidebar */}
      {showActivityFeed && (
        <div className="fixed inset-y-0 left-0 w-80 bg-white shadow-2xl border-r border-slate-200 z-50 flex flex-col" dir="ltr">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-bold text-slate-800 flex items-center gap-2">
              <Activity className="w-5 h-5 text-slate-500" />
              {tx('Recent Activity', 'פעילות אחרונה')}
            </h3>
            <button
              onClick={() => setShowActivityFeed(false)}
              className="p-1.5 hover:bg-slate-100 rounded-lg"
            >
              <X className="w-4 h-4 text-slate-400" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {activityFeed.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Activity className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{tx('No recent activity', 'אין פעילות אחרונה')}</p>
              </div>
            ) : (
              activityFeed.map((activity) => (
                <div
                  key={activity.id}
                  className="flex items-start gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                  onClick={() => {
                    onEditProposal?.(activity.proposal.id)
                    setShowActivityFeed(false)
                  }}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                    activity.type === 'signed' ? 'bg-emerald-100 text-emerald-600' :
                    activity.type === 'viewed' ? 'bg-amber-100 text-amber-600' :
                    'bg-slate-100 text-slate-700'
                  }`}>
                    {activity.type === 'signed' ? <CheckCircle2 className="w-4 h-4" /> :
                     activity.type === 'viewed' ? <Eye className="w-4 h-4" /> :
                     <Send className="w-4 h-4" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {activity.proposal.proposal_title}
                    </p>
                    <p className="text-xs text-slate-500">
                      {activity.type === 'signed' ? tx('Signed', 'נחתם') :
                       activity.type === 'viewed' ? tx('Viewed', 'נצפה') : tx('Sent', 'נשלח')}
                      {' • '}
                      {formatRelativeTime(activity.date.toISOString())}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Modal */}
      {showShortcuts && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" dir="ltr">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-bold text-slate-800 flex items-center gap-2">
                <Keyboard className="w-5 h-5 text-slate-500" />
                {tx('Keyboard Shortcuts', 'קיצורי מקלדת')}
              </h3>
              <button
                onClick={() => setShowShortcuts(false)}
                className="p-1.5 hover:bg-slate-100 rounded-lg"
              >
                <X className="w-4 h-4 text-slate-400" />
              </button>
            </div>
            <div className="p-5 space-y-3">
              <ShortcutRow keys={['N']} description={tx('New Proposal', 'הצעה חדשה')} />
              <ShortcutRow keys={['/']} description={tx('Search', 'חיפוש')} />
              <ShortcutRow keys={['Ctrl', 'A']} description={tx('Select All', 'בחר הכל')} />
              <ShortcutRow keys={['Esc']} description={tx('Cancel / Close', 'ביטול / סגירה')} />
              <ShortcutRow keys={['?']} description={tx('Show Shortcuts', 'הצג קיצורים')} />
            </div>
            <div className="p-4 bg-slate-50 border-t border-slate-200">
              <p className="text-xs text-slate-500 text-center">
                {tx('Press', 'לחץ')} <kbd className="px-1.5 py-0.5 bg-slate-200 rounded text-[10px] font-mono">Esc</kbd> {tx('to close', 'לסגירה')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Template Picker Modal */}
      {showTemplates && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" dir="ltr">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-700 text-white p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg">
                    <Plus className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold">{tx('Choose a Template', 'בחר תבנית')}</h2>
                    <p className="text-white/70 text-sm">{tx('Start with a ready template or from scratch', 'התחל עם תבנית מוכנה או מאפס')}</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowTemplates(false)}
                  className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Zap className="w-4 h-4 text-slate-500" />
                <span>{tx('Choose a template for a quick start', 'בחר תבנית להתחלה מהירה')}</span>
              </div>
              <button
                onClick={() => {
                  setShowTemplates(false)
                  onNewProposal?.([])
                }}
                className="text-sm text-slate-700 font-medium hover:text-slate-800"
              >
                {tx('or start from scratch', 'או התחל מאפס')} →
              </button>
            </div>

            {/* Templates Grid */}
            <div className="p-6 overflow-y-auto max-h-[50vh]">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {proposalTemplates.filter(t => t.id !== 'empty').map((template) => (
                  <button
                    key={template.id}
                    onClick={() => {
                      const newBlocks = template.blocks.map((block, index) => ({
                        ...block,
                        id: `block-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 11)}`,
                        order: index
                      }))
                      setShowTemplates(false)
                      onNewProposal?.(newBlocks)
                    }}
                    className="group relative rounded-xl overflow-hidden border border-slate-200 hover:border-slate-300 hover:shadow-lg transition-all"
                  >
                    {/* Solid Color Background */}
                    <div className={`relative h-24 ${templateColors[template.color] || templateColors.blue} flex items-center justify-center`}>
                      <div className="p-3 bg-white/20 rounded-lg text-white group-hover:scale-110 transition-transform">
                        {templateIcons[template.icon] || <FileText className="w-5 h-5" />}
                      </div>
                      <div className="absolute top-2 left-2 px-2 py-0.5 bg-black/20 rounded text-[10px] font-medium text-white">
                        {template.blocks.length} {tx('blocks', 'בלוקים')}
                      </div>
                    </div>
                    {/* Info */}
                    <div className="bg-white p-3 text-right">
                      <h3 className="font-medium text-slate-800 group-hover:text-slate-700 transition-colors">
                        {template.nameHe}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5 truncate">
                        {template.descriptionHe}
                      </p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-slate-200 p-4 bg-white flex items-center justify-between">
              <span className="text-sm text-slate-500">
                {proposalTemplates.length - 1} {tx('templates available', 'תבניות זמינות')}
              </span>
              <button
                onClick={() => setShowTemplates(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                {tx('Cancel', 'ביטול')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comments Panel Modal */}
      {showCommentsPanel && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" dir="ltr">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[80vh] flex flex-col overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-slate-700 to-slate-700 text-white p-5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2">
                <MessageSquare className="w-5 h-5" />
                <h3 className="font-bold text-lg">{tx('Proposal Comments', 'תגובות על ההצעה')}</h3>
              </div>
              <button
                onClick={() => {
                  setShowCommentsPanel(null)
                  setPanelComments([])
                  setOwnerReply('')
                }}
                className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Comments Thread */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50">
              {panelCommentsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-slate-500 animate-spin" />
                </div>
              ) : panelComments.length === 0 ? (
                <div className="text-center py-8">
                  <MessageSquare className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 text-sm">{tx('No comments yet', 'אין תגובות עדיין')}</p>
                </div>
              ) : (
                panelComments.map((comment) => (
                  <div
                    key={comment.id}
                    className={`flex ${comment.is_owner ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                      comment.is_owner
                        ? 'bg-slate-500 text-white rounded-tl-sm'
                        : 'bg-blue-100 text-blue-900 rounded-tr-sm'
                    }`}>
                      <div className={`flex items-center gap-2 mb-1 ${
                        comment.is_owner ? 'text-slate-100' : 'text-blue-700'
                      }`}>
                        <span className="text-xs font-semibold">{comment.author_name}</span>
                        {comment.is_owner && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-400 text-white">
                            {tx('You', 'אתה')}
                          </span>
                        )}
                        {!comment.is_owner && comment.author_email && (
                          <span className="text-[10px] text-blue-500">{comment.author_email}</span>
                        )}
                      </div>
                      <p className="text-sm whitespace-pre-wrap leading-relaxed">{comment.content}</p>
                      <p className={`text-[10px] mt-1 ${
                        comment.is_owner ? 'text-slate-200' : 'text-blue-500'
                      }`}>
                        {formatRelativeTime(comment.created_at)}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Reply Input */}
            <div className="border-t border-slate-200 p-4 bg-white shrink-0">
              <div className="flex gap-2">
                <textarea
                  value={ownerReply}
                  onChange={(e) => setOwnerReply(e.target.value)}
                  placeholder={tx('Write a reply...', 'כתוב תשובה...')}
                  rows={2}
                  className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-400 transition-all"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleSendOwnerReply()
                    }
                  }}
                />
                <button
                  onClick={handleSendOwnerReply}
                  disabled={sendingReply || !ownerReply.trim()}
                  className="self-end p-2.5 bg-gradient-to-br from-slate-700 to-slate-700 text-white rounded-lg hover:from-slate-800 hover:to-slate-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {sendingReply ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Sidebar Item Component - Dark theme with high-tech colors
function SidebarItem({
  icon,
  label,
  active = false,
  badge,
  isNew,
  onClick
}: {
  icon: React.ReactNode
  label: string
  active?: boolean
  badge?: number
  isNew?: boolean
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all
        lg:flex-col lg:gap-1 lg:py-3
        ${active
          ? 'bg-slate-500/10 text-slate-400'
          : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
        }
      `}
    >
      <div className="relative">
        {icon}
        {badge && (
          <span className="absolute -top-1 -left-1 w-4 h-4 bg-orange-500 text-white text-[10px] rounded-full flex items-center justify-center">
            {badge}
          </span>
        )}
        {isNew && (
          <span className="absolute -top-1 -left-1 w-4 h-4 bg-slate-500 text-white text-[8px] rounded-full flex items-center justify-center font-bold">
            ✨
          </span>
        )}
      </div>
      <span className="text-sm lg:text-xs font-medium">{label}</span>
    </button>
  )
}

// Helper function to format relative time in English
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minutes ago`
  if (diffHours < 24) return `${diffHours} hours ago`
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return date.toLocaleDateString('en-US')
}

// Progress Steps Component
function ProgressSteps({ currentStatus }: { currentStatus: string }) {
  const steps = [
    { id: 'draft', label: 'Draft' },
    { id: 'sent', label: 'Sent' },
    { id: 'viewed', label: 'Viewed' },
    { id: 'signed', label: 'Signed' },
  ]

  const statusOrder: Record<string, number> = {
    draft: 0,
    sent: 1,
    viewed: 2,
    signed: 3,
    approved: 3,
  }

  const currentIndex = statusOrder[currentStatus] ?? 0

  return (
    <div className="flex items-center gap-1 mt-3">
      {steps.map((step, index) => {
        const isCompleted = index <= currentIndex
        const isCurrent = index === currentIndex

        return (
          <div key={step.id} className="flex items-center">
            <div className={`w-2 h-2 rounded-full transition-all ${
              isCompleted
                ? isCurrent
                  ? 'bg-slate-500 ring-4 ring-slate-100'
                  : 'bg-slate-500'
                : 'bg-slate-200'
            }`} />
            {index < steps.length - 1 && (
              <div className={`w-8 h-0.5 ${
                index < currentIndex ? 'bg-slate-500' : 'bg-slate-200'
              }`} />
            )}
          </div>
        )
      })}
    </div>
  )
}

// Proposal Card Component - Enhanced with progress bar and high-tech colors
function ProposalCard({
  proposal,
  isSelected = false,
  onSelect,
  isMenuOpen,
  onMenuToggle,
  onEdit,
  onDelete,
  onCopyLink,
  onSendReminder,
  unreadComments = 0,
  onCommentsClick
}: {
  proposal: Proposal
  isSelected?: boolean
  onSelect?: () => void
  isMenuOpen: boolean
  onMenuToggle: () => void
  onEdit: () => void
  onDelete: () => void
  onCopyLink: () => void
  onSendReminder: () => void
  unreadComments?: number
  onCommentsClick?: () => void
}) {
  const status = statusConfig[proposal.status] || statusConfig.draft
  const hasBeenViewed = proposal.view_count > 0
  const isSigned = proposal.status === 'signed' || proposal.signed_at
  const isHotLead = proposal.status === 'viewed' || (proposal.status === 'sent' && hasBeenViewed)

  // Analytics tooltip state
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [analytics, setAnalytics] = useState<ProposalAnalytics | null>(null)
  const [loadingAnalytics, setLoadingAnalytics] = useState(false)
  const analyticsRef = useRef<HTMLDivElement>(null)

  // Audit trail modal state
  const [showAuditTrail, setShowAuditTrail] = useState(false)

  const fetchAnalytics = async () => {
    if (analytics || loadingAnalytics) return
    setLoadingAnalytics(true)
    try {
      const data = await api.getProposalAnalytics(proposal.id)
      setAnalytics(data)
    } catch {
      // Silently fail
    } finally {
      setLoadingAnalytics(false)
    }
  }

  const handleViewCountClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!showAnalytics) {
      fetchAnalytics()
    }
    setShowAnalytics(!showAnalytics)
  }

  // Close analytics popup when clicking outside
  useEffect(() => {
    if (!showAnalytics) return
    const handleClick = (e: MouseEvent) => {
      if (analyticsRef.current && !analyticsRef.current.contains(e.target as Node)) {
        setShowAnalytics(false)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [showAnalytics])

  const formatDuration = (seconds: number): string => {
    if (seconds < 60) return `${seconds} seconds`
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    if (mins < 60) return `${mins} minutes ${secs > 0 ? `${secs} seconds` : ''}`
    const hrs = Math.floor(mins / 60)
    const remainingMins = mins % 60
    return `${hrs} hours ${remainingMins > 0 ? `${remainingMins} minutes` : ''}`
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect?.()}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect?.() } }}
      className={`bg-white rounded-2xl border transition-all relative group cursor-pointer ${
        isSelected ? 'ring-2 ring-slate-500 ring-offset-2 shadow-lg' : ''
      } ${
        isHotLead
          ? 'border-orange-200 hover:border-orange-300 hover:shadow-lg hover:shadow-orange-500/10'
          : isSigned
            ? 'border-emerald-200 hover:border-emerald-300 hover:shadow-lg hover:shadow-emerald-500/10'
            : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
      }`}
    >
      <div className="p-4 lg:p-5">
        <div className="flex items-start gap-4">
          {/* Icon with status indicator */}
          <div className="relative">
            <div className={`w-14 h-14 rounded-xl flex items-center justify-center shrink-0 ${
              isSigned
                ? 'bg-gradient-to-br from-emerald-500 to-teal-500'
                : isHotLead
                  ? 'bg-gradient-to-br from-orange-500 to-amber-500'
                  : hasBeenViewed
                    ? 'bg-gradient-to-br from-amber-400 to-orange-400'
                    : 'bg-gradient-to-br from-slate-100 to-slate-200'
            }`}>
              {isSigned ? (
                <CheckCircle2 className="w-7 h-7 text-white" />
              ) : (
                <FileText className={`w-7 h-7 ${hasBeenViewed || isHotLead ? 'text-white' : 'text-slate-500'}`} />
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-slate-800 truncate text-lg">{proposal.proposal_title}</h3>
                <p className="text-sm text-slate-500 mt-0.5">
                  {proposal.client_name}
                  {proposal.client_company && ` • ${proposal.client_company}`}
                </p>
              </div>

              {/* Status Badge */}
              <span className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 ${status.bg} ${status.text}`}>
                {status.icon}
                {status.label}
              </span>
            </div>

            {/* Progress Bar */}
            <ProgressSteps currentStatus={proposal.status} />

            {/* Proposal Info Row */}
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <span className="px-2.5 py-1 bg-slate-100 text-slate-600 text-xs rounded-lg font-mono">
                {proposal.proposal_number}
              </span>

              {/* View Count - Clickable with Analytics Tooltip */}
              <div className="relative" ref={analyticsRef}>
                <button
                  onClick={handleViewCountClick}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium cursor-pointer transition-all ${
                    hasBeenViewed
                      ? 'bg-cyan-50 text-cyan-700 hover:bg-cyan-100'
                      : 'bg-slate-50 text-slate-400 hover:bg-slate-100'
                  }`}
                  title="Click for view details"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span>{proposal.view_count || 0} views</span>
                  {hasBeenViewed && <BarChart3 className="w-3 h-3 opacity-50" />}
                </button>

                {/* Analytics Popup */}
                {showAnalytics && (
                  <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="text-sm font-bold text-slate-800 flex items-center gap-1.5">
                        <BarChart3 className="w-4 h-4 text-slate-500" />
                        View Analytics
                      </h4>
                      <button onClick={() => setShowAnalytics(false)} className="p-1 hover:bg-slate-100 rounded">
                        <X className="w-3.5 h-3.5 text-slate-400" />
                      </button>
                    </div>

                    {loadingAnalytics ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
                      </div>
                    ) : analytics ? (
                      <div className="space-y-3">
                        {/* Stats Grid */}
                        <div className="grid grid-cols-2 gap-2">
                          <div className="bg-cyan-50 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-cyan-700">{analytics.total_views}</div>
                            <div className="text-[10px] text-cyan-600">Total Views</div>
                          </div>
                          <div className="bg-slate-50 rounded-lg p-2 text-center">
                            <div className="text-lg font-bold text-slate-800">{analytics.unique_views}</div>
                            <div className="text-[10px] text-slate-700">Unique Viewers</div>
                          </div>
                        </div>

                        {/* Avg Duration */}
                        {analytics.avg_duration > 0 && (
                          <div className="flex items-center gap-2 px-2 py-1.5 bg-slate-50 rounded-lg">
                            <Timer className="w-3.5 h-3.5 text-slate-500" />
                            <span className="text-xs text-slate-800">
                              Avg. view time: <strong>{formatDuration(Math.round(analytics.avg_duration))}</strong>
                            </span>
                          </div>
                        )}

                        {/* Device Breakdown */}
                        {Object.keys(analytics.views_by_device).length > 0 && (
                          <div>
                            <div className="text-[10px] font-medium text-slate-500 mb-1.5">Devices</div>
                            <div className="flex items-center gap-2">
                              {analytics.views_by_device.desktop !== undefined && analytics.views_by_device.desktop > 0 && (
                                <div className="flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded">
                                  <Monitor className="w-3 h-3" />
                                  {analytics.views_by_device.desktop}
                                </div>
                              )}
                              {analytics.views_by_device.mobile !== undefined && analytics.views_by_device.mobile > 0 && (
                                <div className="flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded">
                                  <Smartphone className="w-3 h-3" />
                                  {analytics.views_by_device.mobile}
                                </div>
                              )}
                              {analytics.views_by_device.tablet !== undefined && analytics.views_by_device.tablet > 0 && (
                                <div className="flex items-center gap-1 text-[11px] text-slate-600 bg-slate-50 px-2 py-1 rounded">
                                  <Tablet className="w-3 h-3" />
                                  {analytics.views_by_device.tablet}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Last Viewed */}
                        {analytics.viewed_at && (
                          <div className="text-[11px] text-slate-500 border-t border-slate-100 pt-2">
                            Last viewed: {formatRelativeTime(analytics.viewed_at)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-slate-400 text-center py-2">No data</p>
                    )}
                  </div>
                )}
              </div>

              {/* Hot Lead Badge - inline with metadata row */}
              {isHotLead && (
                <span className="px-2.5 py-1 bg-orange-100 text-orange-700 text-xs rounded-lg flex items-center gap-1.5 font-medium">
                  <Flame className="w-3.5 h-3.5" />
                  Hot Lead
                </span>
              )}

              {/* Last Viewed */}
              {proposal.viewed_at && (
                <div className="flex items-center gap-1.5 text-slate-500 text-xs">
                  <Clock className="w-3.5 h-3.5 text-amber-500" />
                  <span>{formatRelativeTime(proposal.viewed_at)}</span>
                </div>
              )}

              {/* Signature Badge */}
              {proposal.signed_at && (
                <span className="px-2.5 py-1 bg-emerald-100 text-emerald-700 text-xs rounded-lg flex items-center gap-1.5 font-medium">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Signed by {proposal.signer_name}
                </span>
              )}

              {/* Comments Badge */}
              {unreadComments > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onCommentsClick?.()
                  }}
                  className="px-2.5 py-1 bg-slate-100 text-slate-800 text-xs rounded-lg flex items-center gap-1.5 font-medium hover:bg-slate-200 transition-colors"
                  title="New comments"
                >
                  <MessageSquare className="w-3.5 h-3.5" />
                  {unreadComments} new comments
                </button>
              )}

              {/* Audit Trail Button */}
              {isSigned && (
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowAuditTrail(true)
                  }}
                  className="p-1.5 bg-slate-50 hover:bg-slate-100 text-slate-700 rounded-lg transition-colors"
                  title="Signature audit trail"
                >
                  <ShieldCheck className="w-4 h-4" />
                </button>
              )}

              {/* Price */}
              <div className="mr-auto font-bold text-slate-800 text-xl">
                {proposal.total_price ? `${proposal.currency}${proposal.total_price.toLocaleString()}` : '—'}
              </div>
            </div>
          </div>

          {/* Menu Button */}
          <div className="relative" data-menu-container>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onMenuToggle()
              }}
              className="p-2 hover:bg-slate-100 rounded-lg transition-colors sm:opacity-0 sm:group-hover:opacity-100"
            >
              <MoreVertical className="w-5 h-5 text-slate-400" />
            </button>

            {/* Dropdown Menu */}
            {isMenuOpen && (
              <div className="absolute right-0 sm:left-0 sm:right-auto top-full mt-2 w-40 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50" onClick={(e) => e.stopPropagation()}>
                <MenuItem icon={<Pencil />} label="Edit" onClick={onEdit} />
                <MenuItem icon={<Copy />} label="Duplicate" onClick={onEdit} />
                <MenuItem icon={<Eye />} label="View Proposal" onClick={() => proposal.share_token && window.open(`/view/${proposal.share_token}`, '_blank')} />
                <MenuItem icon={<Download />} label="Download PDF" onClick={() => proposal.share_token && window.open(`/view/${proposal.share_token}?print=1`, '_blank')} />
                <MenuItem icon={<Link2 />} label="Copy Link" onClick={onCopyLink} />
                {(proposal.status === 'sent' || proposal.status === 'viewed') && (
                  <MenuItem icon={<Bell />} label="Send Reminder" onClick={onSendReminder} />
                )}
                <div className="border-t border-slate-100 my-1" />
                <MenuItem icon={<Trash2 />} label="Delete" danger onClick={onDelete} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audit Trail Modal */}
      {showAuditTrail && (
        <AuditTrailModal
          isOpen={showAuditTrail}
          onClose={() => setShowAuditTrail(false)}
          proposalId={proposal.id}
          proposalTitle={proposal.proposal_title}
        />
      )}
    </div>
  )
}

// Menu Item Component - Clean style with small icons
function MenuItem({
  icon,
  label,
  danger = false,
  onClick
}: {
  icon: React.ReactNode
  label: string
  danger?: boolean
  onClick?: () => void
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors ${
        danger
          ? 'text-rose-600 hover:bg-rose-50'
          : 'text-slate-600 hover:bg-slate-50 hover:text-slate-800'
      }`}
    >
      <span className="w-3.5 h-3.5 [&>svg]:w-3.5 [&>svg]:h-3.5">{icon}</span>
      {label}
    </button>
  )
}

// Shortcut Row Component for keyboard shortcuts modal
function ShortcutRow({ keys, description }: { keys: string[]; description: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-sm text-slate-600">{description}</span>
      <div className="flex items-center gap-1">
        {keys.map((key, i) => (
          <span key={i}>
            <kbd className="px-2 py-1 bg-slate-100 border border-slate-200 rounded text-xs font-mono text-slate-700">
              {key}
            </kbd>
            {i < keys.length - 1 && <span className="text-slate-400 mx-1">+</span>}
          </span>
        ))}
      </div>
    </div>
  )
}
